import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  Calendar,
  Copy,
  Check,
  ExternalLink,
  Users,
  X,
  Pencil,
} from "lucide-react";
import StatusPushPanel from "../../components/StatusPushPanel";
import SharePanel from "../../components/SharePanel";
import EmployerNavBar from "../../components/EmployerNavBar";
import { statusClasses, ROLE_CLASSES } from "../../constants/statusColors";

const VALID_STATUSES = [
  "Applied",
  "Screening",
  "Interview",
  "Technical test",
  "Final interview",
  "Offer received",
  "Accepted",
  "Rejected",
  "Withdrawn",
];

export default function JobPositionDetail() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const { employerUser, employerToken } = useAuthStore();

  const [position, setPosition] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [selectedShareId, setSelectedShareId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [bulkPushOpen, setBulkPushOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkPushing, setBulkPushing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const fetchData = async () => {
    try {
      const [posRes, subRes, sharesRes] = await Promise.all([
        api.get(`/job-positions/${positionId}`, authConfig),
        api.get(`/job-positions/${positionId}/submissions`, authConfig),
        employerUser?.is_agency
          ? api.get(`/job-positions/${positionId}/shares`, authConfig)
          : Promise.resolve({ data: [] }),
      ]);
      setPosition(posRes.data);
      setSubmissions(subRes.data);
      setShares(sharesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Joborigo — Position";
    fetchData();
  }, [positionId]);

  const submissionUrl = position
    ? `${window.location.origin}/submit/${position.submission_token}`
    : "";

  const copyUrl = () => {
    navigator.clipboard.writeText(submissionUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatLocation = () => {
    if (!position) return "";
    const parts = [];
    if (position.location_city) parts.push(position.location_city);
    if (position.location_country) parts.push(position.location_country);
    if (position.is_remote) parts.push("Remote");
    return parts.join(" · ") || "—";
  };

  const formatSalary = () => {
    if (!position?.salary_min && !position?.salary_max) return null;
    const cur = position.salary_currency || "EUR";
    if (position.salary_min && position.salary_max) {
      return `${cur} ${position.salary_min.toLocaleString()} – ${position.salary_max.toLocaleString()}`;
    }
    return `${cur} ${(position.salary_min || position.salary_max).toLocaleString()}+`;
  };

  // Group submissions by current_status
  const groupedSubmissions = VALID_STATUSES.reduce((acc, status) => {
    acc[status] = submissions.filter((s) => s.current_status === status);
    return acc;
  }, {});

  const formatRelative = (iso) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const handleSignOut = () => {
    useAuthStore.getState().clearEmployerAuth();
    navigate("/employers/login");
  };

  const handleBulkPush = async () => {
    if (!bulkStatus) return;
    setBulkPushing(true);
    try {
      const { data } = await api.post(
        `/employer/positions/${positionId}/bulk-status-push`,
        { position_id: positionId, status: bulkStatus, note: bulkNote || null },
        authConfig,
      );
      setBulkResult(data);
      setBulkPushOpen(false);
      setBulkStatus("");
      setBulkNote("");
      fetchData(); // refresh submissions
    } catch (err) {
      alert(err.response?.data?.detail ?? "Bulk push failed.");
    } finally {
      setBulkPushing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-brand-dark-bg">
        <LoadingSpinner />
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100 px-6 py-20 text-center">
        <p>Position not found.</p>
        <button
          onClick={() => navigate("/employers/positions")}
          className="mt-4 text-brand-violet dark:text-brand-violet-on-dark hover:underline"
        >
          Back to positions
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-5xl mx-auto px-6 py-8">
        <button
          onClick={() => navigate("/employers/positions")}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to positions
        </button>

        {/* Position header */}
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              {position.client_company_name && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Client: {position.client_company_name}
                  {!position.client_visible_to_candidates && (
                    <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                      (hidden from candidates)
                    </span>
                  )}
                </p>
              )}
              <h1 className="text-2xl font-bold mb-3 truncate">
                {position.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-bg text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {position.employment_type}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-bg text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {formatLocation()}
                </span>
                {formatSalary() && (
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-brand-dark-bg text-gray-700 dark:text-gray-300">
                    {formatSalary()}
                  </span>
                )}
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium ${
                    position.status === "active"
                      ? ROLE_CLASSES.active
                      : ROLE_CLASSES.neutral
                  }`}
                >
                  {position.status}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end text-right">
              <button
                onClick={() =>
                  navigate(`/employers/positions/${positionId}/edit`)
                }
                className="mb-3 flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-white/10 text-sm rounded-md hover:bg-gray-50 dark:hover:bg-white/5 transition text-gray-700 dark:text-gray-300"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
              <span className="flex items-center gap-1.5 text-2xl font-bold text-brand-violet dark:text-brand-violet-on-dark">
                <Users className="w-5 h-5" />
                {submissions.length}
              </span>
              <span className="text-xs text-gray-400">
                {submissions.length === 1 ? "candidate" : "candidates"}
              </span>
            </div>
          </div>

          {/* Submission link */}
          <div className="bg-gray-50 dark:bg-brand-dark-bg rounded-lg p-3 flex items-center gap-2">
            <input
              type="text"
              value={submissionUrl}
              readOnly
              className="flex-1 bg-transparent text-xs font-mono text-brand-violet dark:text-brand-violet-on-dark break-all"
            />
            <button
              onClick={copyUrl}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy link
                </>
              )}
            </button>
            <a
              href={submissionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-white/10 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-white/5 transition text-gray-700 dark:text-gray-300"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Preview
            </a>
          </div>
        </div>

        {/* Submissions */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Pipeline</h2>
          {submissions.length > 0 && employerUser?.is_agency && (
            <button
              onClick={() => setSharePanelOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
            >
              <Users className="w-3.5 h-3.5" />
              Share with client
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {submissions.length > 0 && (
            <button
              onClick={() => setBulkPushOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark text-sm rounded-md hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition"
            >
              Push status to all
            </button>
          )}
          {submissions.length > 0 && employerUser?.is_agency && (
            <button
              onClick={() => setSharePanelOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
            >
              <Users className="w-3.5 h-3.5" />
              Share with client
            </button>
          )}
        </div>
        {submissions.length === 0 ? (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-dashed border-gray-200 dark:border-white/10 p-12 text-center">
            <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">
              No submissions yet
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Share the link above to start collecting candidates.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {VALID_STATUSES.map((status) => {
              const items = groupedSubmissions[status];
              if (!items || items.length === 0) return null;
              return (
                <div key={status}>
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${statusClasses(status)}`}
                    >
                      {status}
                    </span>
                    <span className="text-xs text-gray-400">
                      {items.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {items.map((sub) => (
                      <button
                        key={sub.application_id}
                        onClick={() => setSelectedSubmission(sub)}
                        className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-4 text-left hover:border-brand-violet/50 dark:hover:border-brand-violet-on-dark/50 transition"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark flex items-center justify-center font-semibold text-sm flex-shrink-0">
                              {sub.candidate_initial}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">
                                {sub.candidate_full_name ||
                                  `Candidate ${sub.candidate_initial}`}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Last activity{" "}
                                {formatRelative(sub.last_activity_at)}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            Submitted {formatRelative(sub.submitted_at)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Shares — only for agencies */}
        {employerUser?.is_agency && shares.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-semibold mb-4">Client shares</h2>
            <div className="space-y-2">
              {shares.map((share) => {
                const status = share.feedback_submitted_at
                  ? "feedback"
                  : share.view_count > 0
                    ? "viewed"
                    : "pending";
                const statusLabel = {
                  feedback: "Feedback received",
                  viewed: "Viewed",
                  pending: "Pending",
                }[status];
                const statusColor = {
                  feedback: ROLE_CLASSES.positive,
                  viewed: ROLE_CLASSES.active,
                  pending: ROLE_CLASSES.neutral,
                }[status];

                return (
                  <button
                    key={share.id}
                    onClick={() => {
                      setSelectedShareId(share.id);
                      setSharePanelOpen(true);
                    }}
                    className="w-full text-left flex items-center gap-3 p-4 bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {share.client_label || "Untitled share"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {new Date(share.created_at).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          },
                        )}{" "}
                        · {share.item_count} candidate
                        {share.item_count !== 1 && "s"} · {share.view_count}{" "}
                        view{share.view_count !== 1 && "s"}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}
                    >
                      {statusLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* Submission detail panel — slide-in */}
      {selectedSubmission && (
        <StatusPushPanel
          submission={selectedSubmission}
          authConfig={authConfig}
          onClose={() => setSelectedSubmission(null)}
          onStatusPushed={() => {
            // Refresh data after a status push
            fetchData();
            setSelectedSubmission(null);
          }}
        />
      )}

      {/* Share panel — slide-in */}
      <SharePanel
        isOpen={sharePanelOpen}
        onClose={() => {
          setSharePanelOpen(false);
          setSelectedShareId(null);
          fetchData(); // refresh shares list in case state changed
        }}
        positionId={positionId}
        submissions={submissions}
        authConfig={authConfig}
        existingShareId={selectedShareId}
      />

      {/* Bulk push modal */}
      {bulkPushOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Push status to all candidates
              </h3>
              <button onClick={() => setBulkPushOpen(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This will push the selected status to all {submissions.length}{" "}
              candidates in this pipeline.
            </p>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-white mb-3"
            >
              <option value="">Select status</option>
              {[
                "Position filled",
                "Applications closed",
                "Process on hold",
                "Not moving forward",
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <textarea
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Optional note to candidates..."
              rows={3}
              className="w-full border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-white mb-4 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setBulkPushOpen(false)}
                className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkPush}
                disabled={!bulkStatus || bulkPushing}
                className="flex-1 bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
              >
                {bulkPushing ? "Pushing..." : "Push to all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk push success banner */}
      {bulkResult && (
        <div className="fixed bottom-6 right-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-5 py-3 shadow-lg z-50 flex items-center gap-3">
          <p className="text-sm text-green-800 dark:text-green-300">
            {bulkResult.detail}
          </p>
          <button
            onClick={() => setBulkResult(null)}
            className="text-green-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
